import resolveConfig from 'tailwindcss/resolveConfig'
import tailwindConfigFile from '@/tailwind.config'

export const tailwindConfig = resolveConfig(tailwindConfigFile) as any

export const getBreakpointValue = (value: string): number => {
  const screenValue = tailwindConfig.theme.screens[value]
  return +screenValue.slice(0, screenValue.indexOf('px'))
}

/**
 * Extracts plain text from HTML by creating a DOM element and getting its text content.
 * This function works only on the client-side.
 *
 * @param html HTML string to extract text from
 * @returns Plain text extracted from HTML
 */
export function stripHtmlClient(html: string): string {
  if (!html) return '';

  // Create a temporary DOM element
  const temp = document.createElement('div');
  temp.innerHTML = html;

  // Get the text content
  return temp.textContent || temp.innerText || '';
}

export function formatPricePerMinute(price: number, duration: number): string | null {
  if (price === undefined || price === null || isNaN(price) || duration <= 0) return null;
  const ppm = Math.floor((price / duration) * 100) / 100;
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(ppm);
}
/**
 * Strips HTML tags from a string in a consistent way that works on both server and client
 * to avoid hydration errors.
 *
 * @param html HTML string to strip tags from
 * @returns Plain text string
 */
export function stripHtmlTags(html: string): string {
  if (!html) return '';

  // Replace common HTML entities first
  let text = html
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');

  // Replace structural elements with spaces
  text = text
    .replace(/<\/h[1-6]>/g, '. ')        // Add period and space after headings
    .replace(/<br\s*\/?>/gi, ' ')        // Replace <br> with space
    .replace(/<\/p>/gi, ' ')             // Replace </p> with space
    .replace(/<\/div>/gi, ' ')           // Replace </div> with space
    .replace(/<\/li>/gi, ' ')            // Replace </li> with space (removed comma)
    .replace(/<\/tr>/gi, ' ')            // Replace </tr> with space
    .replace(/<\/td>/gi, ' ')            // Replace </td> with space
    .replace(/<\/blockquote>/gi, ' ');   // Replace </blockquote> with space

  // Remove all HTML tags
  text = text.replace(/<[^>]*>?/gm, '');

  // Normalize whitespace - critical for consistent results
  text = text
    .replace(/\s+/g, ' ')  // Replace multiple spaces with a single space
    .trim();

  return text;
}

export const getBreakpoint = () => {
  let currentBreakpoint
  let biggestBreakpointValue = 0
  let windowWidth = typeof window !== 'undefined' ? window.innerWidth : 0
  for (const breakpoint of Object.keys(tailwindConfig.theme.screens)) {
    const breakpointValue = getBreakpointValue(breakpoint)
    if (
      breakpointValue > biggestBreakpointValue &&
      windowWidth >= breakpointValue
    ) {
      biggestBreakpointValue = breakpointValue
      currentBreakpoint = breakpoint
    }
  }
  return currentBreakpoint
}

export const hexToRGB = (h: string): string => {
  let r = 0
  let g = 0
  let b = 0
  if (h.length === 4) {
    r = parseInt(`0x${h[1]}${h[1]}`)
    g = parseInt(`0x${h[2]}${h[2]}`)
    b = parseInt(`0x${h[3]}${h[3]}`)
  } else if (h.length === 7) {
    r = parseInt(`0x${h[1]}${h[2]}`)
    g = parseInt(`0x${h[3]}${h[4]}`)
    b = parseInt(`0x${h[5]}${h[6]}`)
  }
  return `${+r},${+g},${+b}`
}

export const formatValue = (value: number): string => Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
  notation: 'compact',
}).format(value)

export const formatThousands = (value: number): string => Intl.NumberFormat('de-DE', {
  maximumSignificantDigits: 3,
  notation: 'compact',
}).format(value)


export const chartAreaGradient = (ctx: CanvasRenderingContext2D, chartArea: { bottom: number; left: number; top: number; right: number; }, gradientStops: { stop: number; color: string }[]) => {
  if (!chartArea) return null;

  const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
  gradientStops.forEach((stop) => {
    gradient.addColorStop(stop.stop, stop.color);
  });

  return gradient;
};
